import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Modal,
  Animated,
  Easing,
} from "react-native";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { observer, Observer } from "mobx-react";
import GlobalStyles from "../../../Resources/Styles";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { Octicons } from "@expo/vector-icons";
import { Entypo } from "@expo/vector-icons";
import { Ionicons } from "@expo/vector-icons";
import { Menu, MenuItem } from "react-native-material-menu";
import { useScreenDimensions, useDevice } from "../../_layout";

import CustomAlert from "../../Components/CustomAlert";
import { useTranslation } from "react-i18next";

import IconEmptyView from "../../Components/IconEmptyView";
import AddBlockView from "../../Block/AddBlockView";
import DeleteBlockView from "../../Block/DeleteBlockView";
import ClientWeekStatusText from "../../Components/ClientWeekStatusText";
import SearchBar from "../../Components/SearchBar";
import CTAButton from "../../Components/CTAButton";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import BackButton from "../../Components/BackButton";
import WorkoutsDashboardTabBar from "../../Components/WorkoutsDashboardTabBar";
import BlockDashboard from "../../Block/BlockDashboard";
import BlockView from "../../Block/shared/BlockView";
import BlockCard from "../../Block/shared/BlockCard";
import { Client } from "../../Firestore/Client";
import { useDrag, useDrop } from "react-dnd";
import { useToast } from "../../contexts/ToastContext";
import { commitBlockLibraryOrder, moveItem } from "./blockLibraryReorderUtils";
import {
  buildPendingMoveLibraryPayload,
  shouldDisablePendingMenuRowInteractions,
} from "./pendingLibraryMoveUtils";
import {
  flattenPendingUserBlocksFromEntries,
  getPendingUserBlocksTotalCount,
  removePendingBlockFromWorkspaceSummaries,
  selectPendingUserBlockEntries,
  shouldShowPendingUserBlocksSection,
} from "./pendingBlocksDiscoverabilityUtils";
import { useModalKeyboardShortcuts } from "../../platform/guards/modalKeyboardShortcuts";
import {
  filterBlockCopyEligibleClients,
  isBlockCopyEligibleClient,
} from "../../invitations/invitedClientUiGuards";

const COACH_LIBRARY_BLOCK_DND_TYPE = "COACH_LIBRARY_BLOCK";
const COACH_LIBRARY_BLOCK_CATEGORY_REGULAR = "regular";
const COACH_LIBRARY_BLOCK_CATEGORY_ORPHAN = "orphan";

const getDOMElement = (ref) => {
  if (!ref) return null;
  if (ref.getBoundingClientRect) return ref;
  if (ref._component) return ref._component;
  if (typeof ref.getNode === "function") return ref.getNode();
  return ref;
};

const hasSameOrder = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
};

const BlockLibrary = observer(({ coach }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const [showingAddNewBlock, setShowingAddNewBlock] = useState(false);
  const [isPendingSectionCollapsed, setIsPendingSectionCollapsed] =
    useState(true);
  const [isPendingSectionHeaderHovered, setIsPendingSectionHeaderHovered] =
    useState(false);
  const [pressSuppressedBlockId, setPressSuppressedBlockId] = useState(null);
  const [pendingMoveInFlightByBlockId, setPendingMoveInFlightByBlockId] =
    useState({});
  const pressSuppressTimeoutRef = useRef(null);
  const dragStartIdsRef = useRef([]);
  const draggedBlockIdRef = useRef(null);
  const pendingChevronSpin = useRef(
    new Animated.Value(isPendingSectionCollapsed ? 0 : 1),
  ).current;
  const pendingSectionEntries = selectPendingUserBlockEntries({
    pendingWorkspaceSummaries: coach?.pendingWorkspaceSummaries,
    clients: coach?.clients,
    invitationSummaries: coach?.invitationSummaries,
  });
  const pendingSectionBlockEntries = useMemo(
    () => flattenPendingUserBlocksFromEntries(pendingSectionEntries),
    [pendingSectionEntries],
  );
  const pendingSectionTotalCount = getPendingUserBlocksTotalCount(
    pendingSectionEntries,
  );
  const shouldShowPendingSection = shouldShowPendingUserBlocksSection(
    pendingSectionEntries,
  );
  const pendingChevronRotation = pendingChevronSpin.interpolate({
    inputRange: [0, 1],
    outputRange: ["180deg", "0deg"],
  });

  const pendingSectionBlocks = useMemo(() => {
    if (!coach || pendingSectionBlockEntries.length === 0) {
      return [];
    }

    const pendingClientsByUserId = new Map();
    const resolvePendingClient = (entry) => {
      const pendingUserId = String(entry?.pendingUserId ?? "").trim();
      if (!pendingUserId) return null;

      const existing = pendingClientsByUserId.get(pendingUserId);
      if (existing) return existing;

      const pendingClient = new Client(
        pendingUserId,
        String(entry?.invitationEmail ?? "").trim(),
        "",
        "",
        "",
        "",
        false,
        false,
        null,
        false,
        null,
        [],
        false,
        0,
        0,
        coach,
        [],
        null,
        "",
        "pending-string",
        String(entry?.invitationId ?? "").trim(),
        [],
        false,
        "",
        "",
        "",
        0,
        0,
        0,
        "none",
        [],
        pendingUserId,
        true,
        String(entry?.invitationEmail ?? "").trim(),
        null,
      );

      pendingClientsByUserId.set(pendingUserId, pendingClient);
      return pendingClient;
    };

    return pendingSectionBlockEntries
      .map((entry) => {
        const pendingClient = resolvePendingClient(entry);
        if (!pendingClient) return null;

        try {
          const block = pendingClient.fetchBlock(entry.blockData);
          if (!block) return null;
          return {
            id: entry.id,
            block,
            invitationEmail: String(entry?.invitationEmail ?? "").trim(),
          };
        } catch (error) {
          console.warn("Skipping invalid pending section block entry:", error);
          return null;
        }
      })
      .filter(Boolean);
  }, [coach, pendingSectionBlockEntries]);

  const setPendingMoveInFlight = useCallback((blockId, isInFlight) => {
    const normalizedBlockId = String(blockId ?? "")
      .trim()
      .toLowerCase();
    if (!normalizedBlockId) return;

    setPendingMoveInFlightByBlockId((prev) => {
      const currentlyInFlight = prev[normalizedBlockId] === true;
      if (currentlyInFlight === isInFlight) {
        return prev;
      }

      if (!isInFlight) {
        const { [normalizedBlockId]: _ignored, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [normalizedBlockId]: true,
      };
    });
  }, []);

  const showBlockLibrarySyncErrorToast = useCallback(() => {
    showToast("syncErrorBlockLibrary-string", {
      type: "error",
      description: "syncErrorBlockLibraryDescription-string",
    });
  }, [showToast]);

  const removeMovedPendingBlockFromLocalSummaries = useCallback(
    ({ pendingUserId, blockId }) => {
      if (!coach) return;

      const nextSummaries = removePendingBlockFromWorkspaceSummaries({
        pendingWorkspaceSummaries: coach.pendingWorkspaceSummaries,
        pendingUserId,
        blockId,
      });

      coach.setProperty("pendingWorkspaceSummaries", nextSummaries);
    },
    [coach],
  );

  useEffect(
    () => () => {
      if (pressSuppressTimeoutRef.current) {
        clearTimeout(pressSuppressTimeoutRef.current);
        pressSuppressTimeoutRef.current = null;
      }
    },
    [],
  );

  const schedulePressSuppressionClear = useCallback(() => {
    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
    }

    pressSuppressTimeoutRef.current = setTimeout(() => {
      setPressSuppressedBlockId(null);
      pressSuppressTimeoutRef.current = null;
    }, 220);
  }, []);

  const resetDragState = useCallback(() => {
    dragStartIdsRef.current = [];
    draggedBlockIdRef.current = null;
  }, []);

  const handleBlockDragStart = useCallback((blockId, orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length < 2) return;

    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
      pressSuppressTimeoutRef.current = null;
    }

    const normalizedBlockId = String(blockId);
    dragStartIdsRef.current = [...orderedIds];
    draggedBlockIdRef.current = normalizedBlockId;
  }, []);

  const handleBlockDrop = useCallback(
    ({ draggedId, dropIndex, draggedCategory, targetCategory }) => {
      const startIds = dragStartIdsRef.current;
      if (!Array.isArray(startIds) || startIds.length === 0) return;
      if (
        draggedCategory !== COACH_LIBRARY_BLOCK_CATEGORY_REGULAR ||
        targetCategory !== COACH_LIBRARY_BLOCK_CATEGORY_REGULAR
      ) {
        return;
      }
      if (!Number.isInteger(dropIndex)) return;
      if (dropIndex < 0 || dropIndex >= startIds.length) return;

      const sourceIndex = startIds.findIndex((id) => id === draggedId);
      if (sourceIndex < 0) return;
      if (sourceIndex === dropIndex) return;

      const finalIds = moveItem(startIds, sourceIndex, dropIndex);
      if (hasSameOrder(startIds, finalIds)) return;

      commitBlockLibraryOrder({
        coach,
        orderedIds: finalIds,
        allowedIds: startIds,
      });
    },
    [coach],
  );

  const handleBlockDragEnd = useCallback(() => {
    const draggedBlockId = draggedBlockIdRef.current;

    resetDragState();
    if (draggedBlockId) {
      setPressSuppressedBlockId(draggedBlockId);
    }
    schedulePressSuppressionClear();
  }, [resetDragState, schedulePressSuppressionClear]);

  useEffect(() => {
    if (!shouldShowPendingSection) {
      setIsPendingSectionCollapsed(true);
    }
  }, [shouldShowPendingSection]);

  useEffect(() => {
    Animated.timing(pendingChevronSpin, {
      toValue: isPendingSectionCollapsed ? 0 : 1,
      duration: 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [isPendingSectionCollapsed, pendingChevronSpin]);

  const CopyBlockToClient = ({ isVisible, onClose, block }) => {
    const [selectedClients, setSelectedClients] = useState([]);

    const [notBlocked, setNotBlocked] = useState([]);
    const resolvedCoach = block?.coach ?? block?.client?.coach ?? null;

    const resolveCopyTargetClients = () => {
      const filteredClients = resolvedCoach?.getFilteredClients?.() ?? [];
      return filterBlockCopyEligibleClients(filteredClients);
    };

    function toggleVariable() {
      setNotBlocked();

      setTimeout(() => {
        setNotBlocked(true);
        onClose();
      }, 2500); // 2000 milliseconds = 2 seconds
    }

    const SearchBar = () => {
      const [keyword, setKeyword] = useState("");
      const onChangeText = (text) => {
        setKeyword(text);
        resolvedCoach?.setSearchTerm(text);
      };

      const [isHovered, setIsHovered] = useState(false);

      return (
        <View
          style={{
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            paddingHorizontal: 20,
            marginTop: 20,
            marginBottom: 10,
          }}
        >
          <View
            style={[
              styles.searchBar,
              {
                backgroundColor: "transparent",
                // width: "50%",
                flex: 1,
                borderWidth: 0.2,
                // borderColor: Colors.grayColor,

                borderColor: isHovered
                  ? "rgba(0, 0, 0, 0.5)"
                  : Colors.borderColor,
              },
            ]}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Octicons name="search" size={20} color={Colors.grayColor} />
            <TextInput
              value={keyword}
              onChangeText={onChangeText}
              style={[
                styles.textInputStyle,
                { color: Colors.blackColor, outline: "none", width: "100%" },
              ]}
              placeholder={t("searchClient-string")}
              placeholderTextColor={Colors.grayColor}
              disabled={!notBlocked}
              maxLength={100}
            />
          </View>
          <Buttons bool={false} />
          <Buttons bool={true} />
        </View>
      );
    };

    const ClientRow = observer(({ client, isLastRow }) => {
      const isSelected = selectedClients.includes(client.id);
      const select = () => {
        if (isSelected) {
          // Append the client's userId to the array
          setSelectedClients((prevSelectedClients) =>
            prevSelectedClients.filter((id) => id !== client.id),
          );
        } else {
          // Remove the client's userId from the array
          setSelectedClients((prevSelectedClients) => [
            ...prevSelectedClients,
            client.id,
          ]);
        }
        resolvedCoach?.setSearchTerm("");
      };

      const [isHovered, setIsHovered] = useState(false);

      return (
        <View
          style={{
            flexDirection: "row",
            flex: 1,
            //marginTop: 20,
            padding: 5,
            paddingHorizontal: 16,

            alignItems: "center",
            // borderRadius: 12,
            backgroundColor: isSelected ? Colors.bubbleColor : "transparent",
            paddingVertical: 8,

            borderBottomLeftRadius: isLastRow ? 14 : 0,
            borderBottomRightRadius: isLastRow ? 14 : 0,

            opacity: isHovered ? 0.9 : 1,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <TouchableOpacity
            onPress={select}
            style={{ flex: 1 }}
            disabled={!notBlocked}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  alignItems: "flex-start",
                  // marginHorizontal: 10,
                  width: "100%",
                  paddingLeft: isHovered ? 5 : 0,
                }}
              >
                <Text
                  style={{
                    color: isSelected ? Colors.whiteColor : Colors.blackColor,
                    fontSize: 16,
                    fontWeight: isHovered ? "600" : "normal",
                  }}
                >
                  {client.nameForCoach} {client.surnameForCoach}
                </Text>
                <ClientWeekStatusText
                  client={client}
                  style={{
                    color: isSelected
                      ? Colors.lightGrayColor
                      : Colors.grayColor,
                    fontSize: 12,
                    marginTop: 2,
                  }}
                  showColorForUnprepared={isSelected ? false : true}
                />
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            />
            <View
              style={{
                //height: selectedClientName === client.name ? 2 : 1,
                backgroundColor: Colors.grayColor,
                // marginTop: 5,
              }}
            />
          </TouchableOpacity>
        </View>
      );
    });

    const Cancel = () => {
      resolvedCoach?.setSearchTerm("");
      onClose();
    };
    const canSave = selectedClients.length > 0;

    const handleSave = async () => {
      if (!canSave) {
        return;
      }

      toggleVariable();

      try {
        const copyPromises = selectedClients.map(async (clientId) => {
          const clientObject = (resolvedCoach?.clients || []).find(
            (cli) => cli.id === clientId,
          );
          const isEligibleCopyTarget = isBlockCopyEligibleClient(clientObject);

          if (!clientObject || !isEligibleCopyTarget) {
            console.warn(
              `Skipping stale or ineligible block copy target with ID: ${clientId}`,
            );
            return;
          }

          await clientObject.copyBlockToClient(t, block, true);
        });

        if (copyPromises.length === 0) return;

        await Promise.all(copyPromises);
      } catch (error) {
        console.error("Error copying block to clients:", error);
      }
    };

    useModalKeyboardShortcuts({
      isVisible,
      onConfirm: handleSave,
      onCancel: Cancel,
      isConfirmEnabled: canSave,
      allowEnterOnSingleLineInput: false,
      isBlocked: () => !notBlocked,
    });

    const Buttons = ({ bool }) => {
      const text = bool ? "save-string" : "cancel-string";

      const [isHovered, setIsHovered] = useState(false);

      return bool ? (
        <TouchableOpacity
          onPress={handleSave}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={!notBlocked}
        >
          <View
            style={{
              // marginHorizontal: 20,
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: Colors.bubbleColor,
              width: 80,
              height: 35,
              justifyContent: "center",
              alignItems: "center",
              opacity: isHovered ? 0.8 : canSave ? 1 : 0.5,
            }}
          >
            {!notBlocked ? (
              <CustomActivityIndicator large={false} white={true} />
            ) : (
              <Text
                style={{
                  textAlignVertical: "center",
                  textAlign: "center",
                  color: Colors.whiteColor,
                }}
              >
                {t(text)}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          onPress={Cancel}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={!notBlocked}
        >
          <View
            style={{
              marginHorizontal: 10,
              backgroundColor: isHovered ? Colors.redColor : Colors.whiteColor,
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: "red",
              width: 80,
              height: 35,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                textAlignVertical: "center",
                textAlign: "center",
                color: isHovered ? "white" : Colors.redColor,
              }}
            >
              {t(text)}
            </Text>
          </View>
        </TouchableOpacity>
      );
    };

    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={notBlocked && onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={Cancel}
            style={GlobalStyles.touchableBackground}
            disabled={!notBlocked}
          >
            <View />
          </TouchableOpacity>
          <View
            style={{
              position: "absolute",
              // height: "100%",
              // width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
            onStartShouldSetResponder={() => false}
          >
            <View
              style={[
                styles.modalView,
                {
                  maxWidth: screenSize === "small" ? 360 : 450,
                  width: "90%",
                  maxHeight: screenSize === "small" ? 450 : 600,
                  alignSelf: "center",
                  // height: "100%",
                },
              ]}
            >
              <SearchBar />
              <View
                style={{
                  marginTop: 10,
                  width: "100%",
                }}
              >
                <Text
                  style={{
                    color: Colors.grayColor,
                    fontWeight: "500",
                    marginLeft: 15,
                    marginBottom: 4,
                  }}
                >
                  {t("athletes-string")}
                </Text>
                <View
                  style={{
                    height: 0.5,

                    // backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backgroundColor: Colors.grayColor,
                  }}
                />
              </View>
              <Observer>
                {() => {
                  const copyTargetClients = resolveCopyTargetClients();

                  return (
                    <ScrollView
                      style={{
                        marginHorizontal: 0,
                        marginBottom: 0,
                        borderBottomLeftRadius: 14,
                        borderBottomRightRadius: 14,
                      }}
                      showsVerticalScrollIndicator={false}
                    >
                      {copyTargetClients.length > 0 ? (
                        <>
                          {copyTargetClients.map((client, index) => (
                            <View key={client.id}>
                              <ClientRow
                                client={client}
                                isLastRow={
                                  index === copyTargetClients.length - 1
                                }
                              />
                              {index < copyTargetClients.length - 1 && (
                                <View
                                  style={{
                                    height: 0.5,
                                    // backgroundColor: "rgba(255, 255, 255, 0.2)",
                                    backgroundColor: Colors.grayColor,
                                    // marginHorizontal: 20,
                                  }}
                                />
                              )}
                            </View>
                          ))}
                        </>
                      ) : (
                        <>
                          <IconEmptyView
                            iconLibrary={"Ionicons"}
                            mainText={"emptyClientsTitle-string"}
                            subText={"noAthletesFoundText-string"}
                            iconName={"people-outline"}
                            marginTop={40}
                            marginBottom={8}
                          />
                        </>
                      )}
                    </ScrollView>
                  );
                }}
              </Observer>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const BlockRow = observer(
    ({
      block,
      dragIndex,
      orderedIds,
      canReorder,
      suppressPress = false,
      menuOnly = false,
      timelineLeadingText = "",
      showActivePulseIndicator = true,
    }) => {
      const blockId = String(block?.id ?? "");
      const normalizedBlockId = blockId.trim().toLowerCase();
      const rowCoach = block?.coach ?? block?.client?.coach ?? coach;
      const isPendingSectionBlock = Boolean(
        menuOnly && block?.client && !block?.coach,
      );
      const rowCategory = isPendingSectionBlock
        ? COACH_LIBRARY_BLOCK_CATEGORY_ORPHAN
        : COACH_LIBRARY_BLOCK_CATEGORY_REGULAR;
      const isMoveInFlight =
        isPendingSectionBlock &&
        pendingMoveInFlightByBlockId[normalizedBlockId] === true;
      const isPendingRowOffline =
        isPendingSectionBlock && rowCoach?.isOffline === true;
      const isPendingRowInteractionDisabled =
        shouldDisablePendingMenuRowInteractions({
          menuOnly,
          isPendingSectionBlock,
          isOffline: isPendingRowOffline,
          isMoveInFlight,
        });

      const [{ isOver }, drop] = useDrop({
        accept: COACH_LIBRARY_BLOCK_DND_TYPE,
        canDrop: (item) => {
          if (!canReorder) return false;
          if (!item || item.type !== COACH_LIBRARY_BLOCK_DND_TYPE) return false;
          if (item.category !== rowCategory) return false;
          return item.id !== blockId;
        },
        drop: (item, monitor) => {
          if (!canReorder) return;
          if (!item || item.type !== COACH_LIBRARY_BLOCK_DND_TYPE) return;
          if (item.category !== rowCategory) return;
          if (monitor.didDrop()) return;
          return { dropIndex: dragIndex, category: rowCategory };
        },
        collect: (monitor) => ({
          isOver: monitor.isOver({ shallow: true }),
        }),
      });

      const [{ isDragging }, drag] = useDrag({
        type: COACH_LIBRARY_BLOCK_DND_TYPE,
        canDrag: () => canReorder,
        item: () => {
          const startIndex = Array.isArray(orderedIds)
            ? orderedIds.findIndex((id) => id === blockId)
            : dragIndex;

          handleBlockDragStart(blockId, orderedIds);

          return {
            type: COACH_LIBRARY_BLOCK_DND_TYPE,
            id: blockId,
            index: startIndex >= 0 ? startIndex : dragIndex,
            category: rowCategory,
          };
        },
        end: (item, monitor) => {
          const dropResult = monitor.getDropResult();
          if (
            canReorder &&
            dropResult &&
            typeof dropResult.dropIndex === "number" &&
            item?.id
          ) {
            handleBlockDrop({
              draggedId: item.id,
              dropIndex: dropResult.dropIndex,
              draggedCategory: item.category,
              targetCategory: dropResult.category,
            });
          }
          handleBlockDragEnd();
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      });

      const dragDropRef = useCallback(
        (node) => {
          const domNode = getDOMElement(node);
          if (!domNode) {
            drag(null);
            drop(null);
            return;
          }
          drag(drop(domNode));
        },
        [drag, drop],
      );

      const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
      const deleteAlertTitle = "deletePendingBlockAlertTitle-string";
      const deleteAlertText = "deletePendingBlockAlertText-string";

      const [showingEditBlock, setShowingEditBlock] = useState(false);
      const [showingCopyToClient, setShowingCopyToClient] = useState(false);

      const action = () => {
        if (menuOnly) {
          return;
        }
        block.setProperty("isSelected", true);
      };

      const deleteLibraryBlock = async () => {
        const isPendingBlock = block?.client && !block?.coach;
        if (isPendingBlock) {
          if (isPendingRowInteractionDisabled) {
            return;
          }

          const deleteResult =
            await block.client.deletePendingBlockTransactional({
              blockId: block?.id,
            });
          if (!deleteResult?.ok) {
            showBlockLibrarySyncErrorToast();
            return;
          }

          removeMovedPendingBlockFromLocalSummaries({
            pendingUserId: block?.client?.pendingUserId,
            blockId: block?.id,
          });

          if (
            rowCoach &&
            typeof rowCoach.refreshPendingWorkspaceSummariesFromCloud ===
              "function"
          ) {
            void rowCoach
              .refreshPendingWorkspaceSummariesFromCloud({
                reason: "pending_block_deleted_from_workspace",
              })
              .catch((error) => {
                console.warn(
                  "[pending-workspace] refresh_after_pending_block_delete_failed",
                  {
                    coachId: rowCoach?.id,
                    pendingUserId: block?.client?.pendingUserId,
                    blockId: block?.id,
                    error: error?.message ?? error,
                  },
                );
              });
          }
          return;
        }

        if (block.coach) {
          block.coach.updateCoachDeletionBlocks(block.id);
          block.coach.startOrResetBatchTimer();
        }

        rowCoach.setProperty(
          "blockLibrary",
          (rowCoach.blockLibrary = rowCoach.blockLibrary.filter(
            (b) => b.id !== block.id,
          )),
        );
      };

      const handleMenuAction = async (menuBlock, itemKey) => {
        if (!menuBlock) return;
        if (menuOnly && itemKey === "moveToLibrary") {
          if (isPendingRowInteractionDisabled) return;
          if (!menuBlock?.client?.coach) return;

          const moveBlockId = String(menuBlock?.id ?? "")
            .trim()
            .toLowerCase();
          if (!moveBlockId) return;
          if (pendingMoveInFlightByBlockId[moveBlockId] === true) return;

          const destinationPayload = buildPendingMoveLibraryPayload({
            block: menuBlock,
            blockLibrary: rowCoach?.blockLibrary,
          });

          if (!destinationPayload) {
            showBlockLibrarySyncErrorToast();
            return;
          }

          const destinationBlockId = String(destinationPayload?.id ?? "")
            .trim()
            .toLowerCase();
          if (!destinationBlockId) {
            showBlockLibrarySyncErrorToast();
            return;
          }

          setPendingMoveInFlight(moveBlockId, true);
          try {
            const moveResult =
              await menuBlock.client.movePendingBlockToLibraryTransactional({
                block: menuBlock,
                destinationBlockId,
                destinationBlockData: destinationPayload,
                showToast,
              });

            if (!moveResult?.ok) {
              showBlockLibrarySyncErrorToast();
              return;
            }

            const movedBlockData =
              moveResult?.movedBlockData ?? destinationPayload;
            const movedBlockId = String(
              moveResult?.movedBlockId ?? destinationBlockId,
            )
              .trim()
              .toLowerCase();

            const hasLibraryCollection = Array.isArray(rowCoach?.blockLibrary);
            const isAlreadyHydratedInLibrary =
              hasLibraryCollection &&
              rowCoach.blockLibrary.some(
                (libraryBlock) =>
                  String(libraryBlock?.id ?? "")
                    .trim()
                    .toLowerCase() === movedBlockId,
              );

            if (movedBlockData && rowCoach && !isAlreadyHydratedInLibrary) {
              try {
                rowCoach.fetchBlock(movedBlockData);
              } catch (error) {
                console.warn(
                  "[pending-block-move] local_library_hydration_failed",
                  {
                    blockId: movedBlockId,
                    error: error?.message ?? error,
                  },
                );
              }
            }

            removeMovedPendingBlockFromLocalSummaries({
              pendingUserId: menuBlock?.client?.pendingUserId,
              blockId: menuBlock?.id,
            });

            if (
              rowCoach &&
              typeof rowCoach.refreshPendingWorkspaceSummariesFromCloud ===
                "function"
            ) {
              void rowCoach
                .refreshPendingWorkspaceSummariesFromCloud({
                  reason: "pending_block_moved_to_library",
                })
                .catch((error) => {
                  console.warn(
                    "[pending-workspace] refresh_after_pending_block_move_failed",
                    {
                      coachId: rowCoach?.id,
                      pendingUserId: menuBlock?.client?.pendingUserId,
                      blockId: menuBlock?.id,
                      error: error?.message ?? error,
                    },
                  );
                });
            }
          } finally {
            setPendingMoveInFlight(moveBlockId, false);
          }
          return;
        }
        if (itemKey === "edit") {
          setShowingEditBlock(true);
          return;
        }
        if (itemKey === "copyToClient") {
          setShowingCopyToClient(true);
          return;
        }
        if (itemKey === "delete") {
          setShowingDeleteAlert(true);
        }
      };

      const blockMenuItems = menuOnly
        ? [
            {
              key: "moveToLibrary",
              label: t("moveToLibrary-string"),
              icon: { type: "ionicons", name: "book-outline" },
              disabled: isPendingRowInteractionDisabled,
            },
            {
              key: "delete",
              label: t("delete-string"),
              icon: { type: "ionicons", name: "trash-outline" },
              isDanger: true,
              disabled: isPendingRowInteractionDisabled,
            },
          ]
        : [
            {
              key: "edit",
              label: t("edit-string"),
              icon: { type: "ionicons", name: "create-outline" },
            },
            {
              key: "copyToClient",
              label: t("copyToAClient-string"),
              icon: { type: "ionicons", name: "person-add-outline" },
            },
            {
              key: "delete",
              label: t("delete-string"),
              icon: { type: "ionicons", name: "trash-outline" },
              isDanger: true,
            },
          ];

      return (
        <>
          <BlockCard
            block={block}
            folderKey="coach-library"
            blockId={block.id}
            screenSize={screenSize}
            i18n={i18n}
            t={t}
            menuItems={blockMenuItems}
            onPress={action}
            onSummaryPress={() => {}}
            onMenuAction={handleMenuAction}
            showRightIcons={
              !(isPendingSectionBlock && isPendingRowInteractionDisabled)
            }
            showSummaryAction={false}
            showInitialDate={false}
            timelineVariant="neutral"
            dragNodeRef={dragDropRef}
            isDragging={isDragging}
            draggingOpacity={0}
            isDropZoneActive={canReorder && isOver && !isDragging}
            suppressPress={suppressPress}
            timelineLeadingText={timelineLeadingText}
            showActivePulseIndicator={showActivePulseIndicator}
            openMenuOnPress={menuOnly && !isPendingRowInteractionDisabled}
            touchableStyle={
              isPendingRowOffline ? styles.pendingBlockCardOffline : null
            }
          />
          {showingCopyToClient && (
            <CopyBlockToClient
              isVisible={showingCopyToClient}
              onClose={() => setShowingCopyToClient(false)}
              block={block}
            />
          )}
          {showingDeleteAlert && (
            <CustomAlert
              isVisible={showingDeleteAlert}
              onClose={() => setShowingDeleteAlert(false)}
              title={deleteAlertTitle}
              text={deleteAlertText}
              textParams={{ itemName: block?.name || "" }}
              action={deleteLibraryBlock}
              specialCTA={"delete-string"}
            />
          )}
          {showingEditBlock && (
            <AddBlockView
              isVisible={showingEditBlock}
              onClose={() => setShowingEditBlock(false)}
              block={block}
              coach={rowCoach}
            />
          )}
        </>
      );
    },
  );

  const LibraryBlockDescription = observer(({ block }) => {
    const [showingDeleteProtocolAlert, setShowingDeleteProtocolAlert] =
      useState(false);

    const [showingCopyToClient, setShowingCopyToClient] = useState(false);

    const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
    const deleteAlertTitle = "deleteBlockAlertTitle-string";
    const deleteAlertText = "deleteBlockAlertText-string";

    const [blockMenu, setBlockMenu] = useState(false);
    const showBlockMenu = () => setBlockMenu(true);
    const shouldShowBlockDashboardSection =
      block?.coach?.shouldShowBlockDashboardSection ?? true;
    const isShowingDashboard =
      shouldShowBlockDashboardSection && block.isShowingDashboard;

    useEffect(() => {
      if (!shouldShowBlockDashboardSection && block.isShowingDashboard) {
        block.setProperty("isShowingDashboard", false);
      }
    }, [block, shouldShowBlockDashboardSection]);

    const handleLibraryTabSelect = useCallback(
      (key) => {
        if (key === "dashboard" && !shouldShowBlockDashboardSection) {
          return;
        }
        block.setProperty("isShowingDashboard", key === "dashboard");
      },
      [block, shouldShowBlockDashboardSection],
    );

    const BlockTitle = observer(() => {
      const [isHovered, setIsHovered] = useState(false);

      const MenuItemView = ({ index }) => {
        const [isHovered, setIsHovered] = useState(false);

        const showCopyToClient = () => {
          setShowingCopyToClient(true);
          setBlockMenu(false);
        };

        const showDeleteAlert = () => {
          setShowingDeleteAlert(true);
          setBlockMenu(false);
        };

        return index === 0 ? (
          <MenuItem
            onPress={showCopyToClient}
            style={{ minWidth: 190 }}
            textStyle={{
              fontSize: 14,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {t("copyToAClient-string")}
          </MenuItem>
        ) : (
          <MenuItem
            onPress={showDeleteAlert}
            style={{ minWidth: 190 }}
            textStyle={{
              fontSize: 14,
              color: "red",
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {t("delete-string")}
          </MenuItem>
        );
      };

      return (
        <View
          style={{
            width: screenSize === "small" && "100%",
            ...(screenSize === "small" ? { flex: 1 } : {}),
            // borderWidth: 1,
            flexShrink: 1,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={[Fonts.bold18, { marginRight: 12 }]}
              numberOfLines={2}
              onPress={() => {
                if (screenSize === "small") {
                  if (block.coach.selectedBlock) {
                    block.coach.setSelectedBlock(null);
                  } else {
                    block.coach.setSelectedBlock(null);
                  }
                }
              }}
            >
              {block.name}
            </Text>

            <Menu
              visible={blockMenu}
              anchor={
                <TouchableOpacity
                  onPress={showBlockMenu}
                  style={{ opacity: isHovered ? 0.5 : 1 }}
                  onMouseEnter={() => !isMobile && setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <Entypo
                    name="dots-three-horizontal"
                    size={18}
                    color="black"
                  />
                </TouchableOpacity>
              }
              style={[GlobalStyles.menu]}
              onRequestClose={() => setBlockMenu(false)}
            >
              <MenuItemView index={0} />
              <MenuItemView index={1} />
            </Menu>
          </View>

          <View
            style={{
              backgroundColor: Colors.blackColor,
              height: 1,
              opacity: 0.2,
              marginTop: 8,
            }}
          />
        </View>
      );
    });

    const deleteLibraryBlock = () => {
      coach.updateCoachDeletionBlocks(block.id);
      coach.startOrResetBatchTimer();
      coach.setProperty(
        "blockLibrary",
        (coach.blockLibrary = coach.blockLibrary.filter(
          (b) => b.id !== block.id,
        )),
      );
      setShowingDeleteAlert(false);
    };

    return (
      <View
        style={{
          // flex: 1,
          justifyContent: "space-between",
        }}
      >
        <View
          style={{
            // flexDirection: screenSize === "large" ? "row" : "column",
            // gap: screenSize === "small" ? 8 : 24,
            width: "100%",
          }}
        >
          {screenSize != "small" && (
            <View style={{ marginBottom: 12 }}>
              <BackButton
                action={() => {
                  block.setProperty("isSelected", false);
                }}
                backString={t("back-string")}
              />
            </View>
          )}

          <View
            style={{
              width: "100%",
              flexDirection: screenSize !== "small" && "row",
              marginBottom: 16,
              alignItems: "center",
            }}
          >
            <BlockTitle block={block} />
            {screenSize != "small" && <View style={{ flex: 1 }} />}
            <View
              style={{
                width: screenSize === "small" && "100%",
                flexDirection: "row",
                marginTop: screenSize === "small" && 16,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {shouldShowBlockDashboardSection && (
                <View
                  style={[
                    GlobalStyles.HStack,
                    {
                      justifyContent: "space-evenly",
                      flexDirection: "row",
                      marginLeft: screenSize === "small" ? 12 : 16,
                    },
                  ]}
                >
                  <WorkoutsDashboardTabBar
                    selectedKey={isShowingDashboard ? "dashboard" : "workouts"}
                    onSelect={handleLibraryTabSelect}
                    workoutsLabel={t("workouts-string")}
                    dashboardLabel={t("dashboard-string")}
                    itemGap={12}
                  />
                </View>
              )}
            </View>
          </View>

          {isShowingDashboard ? (
            <BlockDashboard block={block} />
          ) : (
            <BlockView
              block={block}
              showTutorialLink={false}
              showExerciseHistory={false}
            />
          )}
        </View>

        {showingDeleteAlert && (
          <DeleteBlockView
            isVisible={showingDeleteAlert}
            onClose={() => setShowingDeleteAlert(false)}
            title={deleteAlertTitle}
            text={deleteAlertText}
            action={deleteLibraryBlock}
            nameOfBlock={block ? block.name : ""}
          />
        )}
        {showingCopyToClient && (
          <CopyBlockToClient
            isVisible={showingCopyToClient}
            onClose={() => setShowingCopyToClient(false)}
            block={block}
          />
        )}
      </View>
    );
  });

  return (
    <View
      style={[
        styles.container,
        { marginTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      <Observer>
        {() => {
          const filteredBlocks = coach.getFilteredBlockLibrary();
          const orderedBlockIds = filteredBlocks.map((entry) =>
            String(entry?.id ?? ""),
          );
          const canReorderBlocks =
            !coach.selectedBlock &&
            (coach.searchTermBlockLibrary?.trim?.() ?? "") === "" &&
            filteredBlocks.length > 1;

          return (
            <>
              {coach.selectedBlock ? (
                <LibraryBlockDescription block={coach.selectedBlock} />
              ) : (
                <>
                  <View
                    style={[
                      GlobalStyles.HStack,
                      { marginBottom: 16, maxWidth: "45%" },
                    ]}
                  >
                    <SearchBar
                      text={coach.searchTermBlockLibrary}
                      onChangeText={(text) =>
                        coach.setProperty("searchTermBlockLibrary", text)
                      }
                      placeholder={t("searchBlock-string")}
                      autoFocusOnDesktop={true}
                    />

                    <CTAButton
                      plus={true}
                      text={t("newBlock-string")}
                      action={() => setShowingAddNewBlock(true)}
                      disabled={false}
                    />
                  </View>

                  {shouldShowPendingSection && (
                    <View style={styles.pendingSectionContainer}>
                      <TouchableOpacity
                        onPress={() =>
                          setIsPendingSectionCollapsed(
                            (isCollapsed) => !isCollapsed,
                          )
                        }
                        style={[
                          styles.pendingSectionHeader,
                          isPendingSectionHeaderHovered &&
                            styles.pendingSectionHeaderHovered,
                        ]}
                        onMouseEnter={() =>
                          !isMobile && setIsPendingSectionHeaderHovered(true)
                        }
                        onMouseLeave={() =>
                          setIsPendingSectionHeaderHovered(false)
                        }
                      >
                        <View style={styles.pendingSectionHeaderLeft}>
                          <Ionicons
                            name="folder-outline"
                            size={20}
                            color={Colors.grayColor}
                          />
                          <Text
                            numberOfLines={1}
                            style={styles.pendingSectionTitle}
                          >
                            {t("pendingUserBlocksSectionTitle-string")}
                          </Text>
                          <View style={styles.pendingSectionCountChip}>
                            <Text style={styles.pendingSectionCountChipText}>
                              {pendingSectionTotalCount}
                            </Text>
                          </View>
                        </View>
                        <Animated.View
                          style={[
                            styles.pendingSectionChevronWrap,
                            { transform: [{ rotate: pendingChevronRotation }] },
                          ]}
                        >
                          <Ionicons
                            name="chevron-down"
                            size={16}
                            color={Colors.darkgrayColor}
                          />
                        </Animated.View>
                      </TouchableOpacity>

                      {!isPendingSectionCollapsed && (
                        <View
                          style={[
                            styles.pendingSectionBlocksGrid,
                            screenSize === "small" && { gap: 8 },
                          ]}
                        >
                          {pendingSectionBlocks.map((pendingBlockEntry) => (
                            <BlockRow
                              block={pendingBlockEntry.block}
                              key={pendingBlockEntry.id}
                              dragIndex={-1}
                              orderedIds={[]}
                              canReorder={false}
                              menuOnly={true}
                              suppressPress={false}
                              timelineLeadingText={
                                pendingBlockEntry.invitationEmail
                              }
                              showActivePulseIndicator={false}
                            />
                          ))}
                        </View>
                      )}
                    </View>
                  )}

                  {filteredBlocks.length === 0 ? (
                    <IconEmptyView
                      iconLibrary={"Ionicons"}
                      mainText={"emptyBlocksTitle-string"}
                      subText={"createTemplateBlocksText-string"}
                      iconName={"folder-outline"}
                      marginTop={130}
                    />
                  ) : (
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        flexWrap: "wrap",
                        marginTop: screenSize === "small" ? 0 : 8,
                        gap: screenSize === "small" ? 8 : 12,
                      }}
                    >
                      {filteredBlocks.map((block, index) => (
                        <BlockRow
                          block={block}
                          key={block.id}
                          dragIndex={index}
                          orderedIds={orderedBlockIds}
                          canReorder={canReorderBlocks}
                          suppressPress={
                            pressSuppressedBlockId === String(block?.id ?? "")
                          }
                        />
                      ))}
                    </View>
                  )}

                  {showingAddNewBlock && (
                    <AddBlockView
                      isVisible={showingAddNewBlock}
                      onClose={() => setShowingAddNewBlock(false)}
                      coach={coach}
                    />
                  )}
                </>
              )}
            </>
          );
        }}
      </Observer>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
  },
  statisticLabel: {
    fontSize: 13,
  },
  statisticValue: {
    flex: 1,
    fontSize: 13,
    fontWeight: "500",
    marginLeft: 5,
  },
  textInputStyle: {
    flex: 1,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 0,
    fontWeight: "400",
    fontSize: 14,
  },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    borderRadius: 14,
    height: 40,
  },
  flex1: {
    flex: 1,
  },
  modalView: {
    borderRadius: 18,
    backgroundColor: Colors.whiteColor,
  },
  pendingSectionContainer: {
    borderRadius: 14,
    backgroundColor: "transparent",
    marginBottom: 8,
    overflow: "hidden",
  },
  pendingSectionHeader: {
    minHeight: 44,
    paddingHorizontal: 8,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  pendingSectionHeaderHovered: {
    opacity: 0.72,
  },
  pendingSectionHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  pendingSectionTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    color: Colors.blackColor,
    letterSpacing: 0.2,
    flexShrink: 1,
  },
  pendingSectionCountChip: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "rgba(30, 41, 59, 0.05)",
    alignItems: "center",
    justifyContent: "center",
  },
  pendingSectionCountChipText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  pendingSectionChevronWrap: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  pendingSectionBlocksGrid: {
    marginTop: 0,
    paddingTop: 4,
    paddingHorizontal: 0,
    paddingBottom: 14,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  pendingBlockCardOffline: { opacity: 0.5 },
});

export default BlockLibrary;
